import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import CopyIcon from '@material-ui/icons/FilterNone';
import HelpIcon from '@material-ui/icons/Help';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

import { blackColor, grayColor, whiteColor } from 'assets/jss/material-dashboard-react';
import { isNumber } from 'util';
import $scope from 'consts/scope';
import _ from 'lodash';

const Decimal = require('decimal.js');
const autoParse = require('auto-parse');

const BlackIconButton = withStyles(() => ({
  root: { color: 'rgba(255,255,255,.6)', padding: 6, '&:hover': { color: 'rgba(255,255,255,.9)' } },
}))(IconButton);

const styles = {
  cardBody: { display: 'flex', flexDirection: 'column', padding: '16px 18px', borderRadius: 8, margin: '-0.9375rem -20px' },
  cardSubBody: { padding: 0 },
  cardHeader: { fontSize: 20, marginBottom: 10, color: '#fff', fontWeight: 600 },
  cardSubHeader: { fontSize: 16, marginBottom: 10, color: '#fff', fontWeight: 600 },
  cardDesc: { fontWeight: 300, color: '#9aa0a6', marginBottom: 14, whiteSpace: 'pre-wrap', lineHeight: 1.3 },
  params: { display: 'flex', flexWrap: 'wrap', marginBottom: 8, '&:last-child': { marginBottom: 0 } },
  paramsCol: { flexDirection: 'column', '& $param': { width: '100%' } },
  param: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    marginRight: 24,
    minHeight: 56,
    '& > div:first-child': { marginRight: 12, fontWeight: 500, fontSize: 13, color: grayColor[0], minWidth: 140 },
    '& > div:nth-child(2)': { flex: 1, minWidth: 0 },
    '& .MuiInputBase-input': { color: whiteColor },
  },
  paramForm: { display: 'flex', alignItems: 'center', width: '100%' },
  arrow: { color: blackColor },
  tooltip: { backgroundColor: blackColor, fontSize: '.75em', padding: '8px 16px', whiteSpace: 'pre-wrap' },
  noCardHeader: { margin: 0 },
  stratOption: {
    userSelect: 'none',
    fontSize: 13,
    marginTop: 8,
    marginRight: 12,
    display: 'flex',
    alignItems: 'stretch',
    cursor: 'pointer',
    border: '1px solid #3e4248',
    borderRadius: 7,
    overflow: 'hidden',
    backgroundColor: 'rgba(90,86,86,0.94)',
    opacity: 0.9,
    '&:hover': { opacity: 1 },
  },
  stratOpt: { borderTopLeftRadius: 7, borderBottomLeftRadius: 7, padding: '6px 10px', color: 'white', borderRight: '1px solid #3e4248' },
  active: { border: '2px solid #69abe5', fontWeight: 600 },
  actionBtn: { display: 'flex', justifyContent: 'center', alignItems: 'center', width: 36, height: '100%' },
  removeBtn: { color: '#ff5454' },
  copyBtn: { color: 'white' },
  rowRemoveBtn: { marginLeft: 6, color: '#ff5454' },
  separator: { borderBottom: '1px solid #222', margin: '18px 0', width: '100%' },
  inactive: { textDecorationLine: 'line-through', color: 'white', opacity: 0.6 },
};

const keyFor = (name, parent) => (parent ? `${parent}.${name}` : name);

const parseInput = v => {
  const p = autoParse(v);
  if (typeof p === 'number' && String(v).endsWith('.')) return v;
  if (typeof p === 'number' && String(v) === '-0') return v;
  if (typeof p === 'number') return Decimal(v).toNumber();
  if (typeof p === 'boolean') return p;
  return v;
};

const buildInitialState = (data, parent = null, acc = {}) => {
  data.forEach(item => {
    if (!item) return;
    if (item.type === 'form' && Array.isArray(item.children)) {
      buildInitialState(item.children, item.name, acc);
    } else if (typeof item.name === 'string') {
      acc[keyFor(item.name, parent || null)] = item.value;
    }
  });
  return acc;
};

const optionToLabel = option => {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  return option.label || option.name || option.key || option.value || option.id || '';
};

function renderHighlighted(option, inputValue) {
  const text = optionToLabel(option);
  const parts = parse(text, match(text, inputValue || ''));
  return (
    <div>
      {parts.map((part, i) => (
        <span key={i} style={{ fontWeight: part.highlight ? 700 : 400 }}>{part.text}</span>
      ))}
    </div>
  );
}

// helper to detect plain key value object like { foo: 'bar' }
const isPlainKVObject = obj =>
  obj &&
  typeof obj === 'object' &&
  !Array.isArray(obj) &&
  Object.keys(obj).length > 0 &&
  Object.values(obj).every(v => {
    const t = typeof v;
    return t === 'string' || t === 'number' || t === 'boolean';
  });

function SettingPanel(props) {
  const { classes, data = [], title = '', noCardHeader = true, direction = 'row', isSub = false, hidden, tabDescription, className } = props;

  const [state, setState] = React.useState(() => buildInitialState(data));
  const [typed, setTyped] = React.useState({});
  const addGuardRef = React.useRef(false);

  const [open, setOpen] = React.useState(false);
  const [asyncOpts, setAsyncOpts] = React.useState([]);
  const loading = open && asyncOpts.length === 0;

  React.useEffect(() => {
    const base = buildInitialState(data);
    setState(prev => ({ ...base, ...prev }));
  }, [data]);

  React.useEffect(() => {
    let active = true;
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    (async () => { if (loading) { await sleep(1000); if (active) setAsyncOpts([]); } })();
    return () => { active = false; };
  }, [loading]);

  if (hidden) return null;

  const setValue = (value, name, parent = null) => {
    const k = keyFor(name, parent);
    setState(prev => ({ ...prev, [k]: value }));
  };

  const getValue = (name, parent = null) => {
    const k = keyFor(name, parent);
    return Object.prototype.hasOwnProperty.call(state, k) ? state[k] : '';
  };

  const setTypedValue = (value, name, parent = null) => {
    const k = keyFor(name, parent);
    setTyped(prev => ({ ...prev, [k]: value }));
  };

  const getTypedValue = (name, parent = null) => {
    const k = keyFor(name, parent);
    if (Object.prototype.hasOwnProperty.call(typed, k)) return typed[k];
    const raw = getValue(name, parent);
    if (raw && typeof raw === 'object') return optionToLabel(raw);
    return typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
  };

  const fireBoth = (item, value, tag) => {
    if (typeof item.onInputChanged === 'function') item.onInputChanged(value, item);
    if (typeof item.onChanged === 'function') item.onChanged(value, item);
  };

  const handleFormAddOnce = (formItem, ev) => {
    if (addGuardRef.current) return;
    addGuardRef.current = true;
    ev.preventDefault();

    const kids = formItem.children || [];
    const keyChild = kids.find(k => k && k.name && /label|parameter|key/i.test(k.name)) || kids[0];
    const valChild = kids.find(k => k && k.name && /value/i.test(k.name)) || kids[1];

    const kStr = keyChild ? getTypedValue(keyChild.name, formItem.name).trim() : '';
    const vStr = valChild ? getTypedValue(valChild.name, formItem.name) : '';

    if (keyChild) {
      setValue(kStr, keyChild.name, formItem.name);
      fireBoth(keyChild, kStr, 'form-commit');
    }
    if (valChild) {
      setValue(vStr, valChild.name, formItem.name);
      fireBoth(valChild, vStr, 'form-commit');
    }

    if (typeof formItem.onClick === 'function') {
      try { formItem.onClick({ key: kStr, value: vStr }); } catch (e) { console.log('[SP onClick error]', e); }
    }

    setTimeout(() => {
      if (keyChild) {
        setTypedValue('', keyChild.name, formItem.name);
        setValue('', keyChild.name, formItem.name);
        fireBoth(keyChild, '', 'form-reset');
      }
      if (valChild) {
        setTypedValue('', valChild.name, formItem.name);
        setValue('', valChild.name, formItem.name);
        fireBoth(valChild, '', 'form-reset');
      }
      addGuardRef.current = false;
    }, 150);
  };

  // row-level remove button helper
  const renderRowRemove = item =>
    (item && (item.removable || typeof item.onRemove === 'function')) ? (
      <BlackIconButton
        className={classes.rowRemoveBtn}
        size="small"
        onMouseDown={e => { e.preventDefault(); if (item.onRemove) item.onRemove(); }}
        aria-label="remove"
      >
        <ClearIcon fontSize="small" />
      </BlackIconButton>
    ) : null;

  const createElements = (items, parent = null) =>
    items.map((item, idx) => {
      if (!item || item.hide === true) return null;
      if (item.hiddenKey && item.hiddenKey !== item.name && state[item.hiddenKey] === false) return null;

      const valueStyle = item.valueWidth ? { width: item.valueWidth } : { flex: '1 1 auto', minWidth: 0 };
      let labelEl = null;
      if (item.label) {
        const ls = { padding: '5px 0' }; if (item.labelWidth) ls.width = item.labelWidth;
        labelEl = item.tooltip ? (
          <Tooltip arrow title={item.tooltip} placement="top" classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}>
            <div style={ls}>{item.label}</div>
          </Tooltip>
        ) : <div style={ls}>{item.label}</div>;
      }

      if (item.type === 'form') {
        return (
          <div className={'SP-paramForm ' + classes.paramForm} key={item.name}>
            <div style={{ marginRight: 16, display: 'flex', flexWrap: 'wrap', width: '100%' }}>
              {createElements(item.children || [], item.name)}
            </div>
            <Button
              variant="contained"
              color="primary"
              style={{ width: '100%', marginBottom: 12, height: 48 }}
              onMouseDown={e => handleFormAddOnce(item, e)}
            >
              {item.label}
            </Button>
          </div>
        );
      }

      // KEY field: freeSolo, store as plain string
      if ((item.freeSolo === true || Array.isArray(item.options)) && item.name && /label|parameter|key/i.test(item.name)) {
        const text = getTypedValue(item.name, parent);
        const opts = (item.options || []).filter(o => !_.isNil(o));
        return (
          <div className={'SP-param ' + classes.param} key={item.name + idx}>
            {labelEl}
            <div style={valueStyle}>
              <Autocomplete
                id={item.name + '_free'}
                options={opts}
                getOptionLabel={optionToLabel}
                freeSolo
                value={null}
                inputValue={text}
                onInputChange={(e, v) => {
                  setTypedValue(v, item.name, parent);
                  setValue(v, item.name, parent);
                  fireBoth(item, v, 'typing');
                }}
                onChange={(e, v) => {
                  const s = v == null ? '' : (typeof v === 'string' ? v : optionToLabel(v));
                  setTypedValue(s, item.name, parent);
                  setValue(s, item.name, parent);
                  fireBoth(item, s, 'commit');
                }}
                fullWidth
                style={{ width: '100%' }}
                disableClearable
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                renderOption={(o, s) => renderHighlighted(o, s.inputValue)}
              />
            </div>
            {renderRowRemove(item)}
          </div>
        );
      }

      // pairSelect
      if (item.name === 'pairSelect') {
        const text = getTypedValue(item.name, parent);
        return (
          <div className={'SP-param ' + classes.param} key={item.name + idx}>
            {labelEl}
            <div style={valueStyle}>
              <Autocomplete
                id={item.name + '_select'}
                options={[]}
                getOptionLabel={optionToLabel}
                freeSolo
                value={null}
                inputValue={text}
                onInputChange={(e, v) => {
                  setTypedValue(v, item.name, parent);
                  setValue(v, item.name, parent);
                  fireBoth(item, v, 'typing');
                }}
                onChange={(e, v) => {
                  const s = v == null ? '' : (typeof v === 'string' ? v : optionToLabel(v));
                  setTypedValue(s, item.name, parent);
                  setValue(s, item.name, parent);
                  fireBoth(item, s, 'commit');
                }}
                fullWidth
                style={{ width: '100%' }}
                disableClearable
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {false ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(o, s) => renderHighlighted(o, s.inputValue)}
              />
            </div>
            {renderRowRemove(item)}
          </div>
        );
      }

      if (item.type === 'button') {
        return (
          <div className={'SP-param ' + classes.param} key={item.name + idx}>
            {labelEl}
            <div style={valueStyle}>
              <Button
                variant="contained"
                color="primary"
                style={{ height: 48 }}
                onMouseDown={e => { e.preventDefault(); if (item.onClick) item.onClick(); }}
              >
                {getValue(item.name, parent)}
              </Button>
            </div>
            {renderRowRemove(item)}
          </div>
        );
      }

      if (item.type === 'separator') return <div className={classes.separator} key={item.type + idx} />;
      if (item.type === 'subHeader') return (<div key={item.type + idx}><span className={classes.cardSubHeader}>{item.label}</span></div>);
      if (item.type === 'component') return (<div key={item.type + idx} style={{ width: '100%' }}>{item.component}</div>);

      // list of existing params as an array of entries
      if (Array.isArray(item.value)) {
        return (
          <div style={{ width: '100%' }} key={item.name + '_' + idx}>
            {labelEl}
            <div style={{ width: 'auto', display: 'flex', flexWrap: 'wrap' }}>
              {(item.value || []).map((val, i) => {
                const k = typeof val === 'object' ? (val.key || val.label || val.name || val.parameter || '') : '';
                const v = typeof val === 'object' ? (val.value != null ? val.value : '') : '';
                const text = typeof val === 'object' ? (k && v !== '' ? `${k}: ${v}` : (k || String(v))) : String(val);
                const isActive = item.active ? !!item.active[val] : true;
                const showCopy = (typeof val === 'object' && 'copy' in val) ? !!val.copy : !!item.copy;
                const showRemove = (typeof val === 'object' && 'removable' in val)
                  ? !!val.removable
                  : (typeof item.removable === 'boolean' ? item.removable : !!item.onRemove);

                return (
                  <div
                    className={
                      classes.stratOption + ' ' +
                      (val === item.current ? classes.active : '') +
                      (isActive ? '' : ' ' + classes.inactive)
                    }
                    key={item.name + '_' + idx + '_' + i}
                  >
                    <span
                      className={classes.stratOpt}
                      onMouseDown={e => { e.preventDefault(); if (item.onClick) item.onClick(val, i); }}
                    >
                      {text}
                    </span>

                    {showCopy ? (
                      <BlackIconButton
                        className={classes.actionBtn + ' ' + classes.copyBtn}
                        size="small"
                        onMouseDown={e => { e.preventDefault(); if (item.onCopy) item.onCopy(val, i); }}
                        aria-label="copy"
                      >
                        <CopyIcon fontSize="small" />
                      </BlackIconButton>
                    ) : null}

                    {showRemove ? (
                      <BlackIconButton
                        className={classes.actionBtn + ' ' + classes.removeBtn}
                        size="small"
                        onMouseDown={e => {
                          e.preventDefault();
                          if (item.onRemove) item.onRemove(val, i);
                        }}
                        aria-label="remove"
                      >
                        <ClearIcon fontSize="small" />
                      </BlackIconButton>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // NEW: render plain key value object like { k: v, ... } as removable chips
      if (isPlainKVObject(item.value) && (item.onRemove || item.removable)) {
        const entries = Object.entries(item.value);
        return (
          <div style={{ width: '100%' }} key={item.name + '_' + idx + '_obj'}>
            {labelEl}
            <div style={{ width: 'auto', display: 'flex', flexWrap: 'wrap' }}>
              {entries.map(([k, v], i) => (
                <div className={classes.stratOption} key={item.name + '_' + k}>
                  <span
                    className={classes.stratOpt}
                    onMouseDown={e => { e.preventDefault(); if (item.onClick) item.onClick({ key: k, value: v }, i); }}
                  >
                    {k}: {String(v)}
                  </span>

                  <BlackIconButton
                    className={classes.actionBtn + ' ' + classes.removeBtn}
                    size="small"
                    onMouseDown={e => {
                      e.preventDefault();
                      if (item.onRemove) item.onRemove({ key: k, value: v }, i);
                    }}
                    aria-label="remove"
                  >
                    <ClearIcon fontSize="small" />
                  </BlackIconButton>
                </div>
              ))}
            </div>
          </div>
        );
      }

      if (typeof item.value === 'boolean') {
        return (
          <div className={'SP-param ' + classes.param} key={item.name + idx}>
            {labelEl}
            <div style={valueStyle}>
              <Switch
                checked={!!getValue(item.name, parent)}
                onChange={e => { const v = e.target.checked; setValue(v, item.name, parent); if (item.onChanged) item.onChanged(v, item); }}
                inputProps={{ 'aria-label': 'secondary checkbox' }}
              />
            </div>
            {renderRowRemove(item)}
          </div>
        );
      }

      // small option lists as Select
      if (Array.isArray(item.options) && !(item.freeSolo === true || (item.options.length > 4 && typeof item.options[0] !== 'number'))) {
        return (
          <div className={'SP-param ' + classes.param} key={item.name + idx}>
            {labelEl}
            <div style={valueStyle}>
              <Select
                labelId={item.name + '_select_label'}
                id={item.name + '_select'}
                value={getValue(item.name, parent) || ''}
                onChange={e => { const v = e.target.value; setValue(v, item.name, parent); if (item.onChanged) item.onChanged(v, item); }}
                displayEmpty
                inputProps={{ style: { color: whiteColor, height: 48, padding: '12px 14px' } }}
              >
                <MenuItem value=""><span><em style={{ color: grayColor[6] }}>Please Select</em></span></MenuItem>
                {(item.options || []).map(opt => (
                  <MenuItem value={opt} style={{ color: grayColor[5] }} key={`${item.name}_${opt}`}>
                    <span style={{ color: grayColor[5] }}>{opt}</span>
                  </MenuItem>
                ))}
              </Select>
            </div>
            {renderRowRemove(item)}
          </div>
        );
      }

      // default text or number
      const numeric = typeof item.value === 'number' || (isNumber && isNumber(item.value));
      return (
        <div className={'SP-param ' + classes.param} key={item.name + idx}>
          {labelEl}
          <div style={valueStyle}>
            <TextField
              label=""
              type={numeric ? 'number' : 'text'}
              fullWidth
              inputProps={{ readOnly: item.readonly, inputMode: numeric ? 'numeric' : 'text', pattern: numeric ? '[0-9]*' : '', style: { color: whiteColor, height: 48, padding: '12px 14px', lineHeight: '24px' } }}
              value={getValue(item.name, parent)}
              placeholder={item.placeholder ? item.placeholder : ''}
              disabled={item.disabled}
              onChange={e => {
                const raw = e.target.value;
                const v = numeric ? parseInput(raw) : raw;
                setValue(v, item.name, parent);
                if (item.onChanged) item.onChanged(v, item);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const raw = e.target.value;
                  const v = numeric ? parseInput(raw) : raw;
                  setValue(v, item.name, parent);
                  if (item.onChanged) item.onChanged(v, item);
                }
              }}
            />
          </div>
          {renderRowRemove(item)}
        </div>
      );
    });

  const isSubStyle = {}; if (isSub) { isSubStyle.paddingLeft = 0; isSubStyle.paddingRight = 0; }
  const effectiveDesc = ($scope.tabDescriptions && $scope.tabDescriptions[title]) ? $scope.tabDescriptions[title] : tabDescription;

  return (
    <div
      className={
        props.classes.cardBody +
        ' ' + (noCardHeader ? props.classes.noCardHeader : '') +
        ' ' + (isSub ? props.classes.cardSubBody : '') +
        (className ? ' ' + className : '')
      }
      style={isSubStyle}
    >
      <span className={props.classes.cardDesc}>{effectiveDesc}</span>
      <div className={'SP-params ' + props.classes.params + ' ' + (direction === 'row' ? '' : props.classes.paramsCol)}>
        {createElements(data)}
      </div>
    </div>
  );
}

export default withStyles(styles)(SettingPanel);